// constants.js — All KYC-related constants for the Forwardsflow compliance dashboard
// Single source of truth for enums, labels, and configuration values

// ─── User Roles ─────────────────────────────────────────────────────────
/** @enum {string} */
export const KYC_ROLES = Object.freeze({
  COMPLIANCE_ANALYST: 'bank_compliance_maker',
  MLRO: 'bank_compliance_checker',
  RELATIONSHIP_MANAGER: 'bank_admin',
  AUDITOR: 'platform_admin',
  INVESTOR: 'investor_family_office',
  INVESTOR_DELEGATE: 'investor_individual',
});

// ─── Case Types ─────────────────────────────────────────────────────────
/** @enum {string} */
export const CASE_TYPES = Object.freeze({
  INDIVIDUAL: 'individual',
  CORPORATE: 'corporate',
  TRUST: 'trust',
  FUND: 'fund',
  FAMILY_OFFICE: 'family_office',
  SPE: 'spe',
});

export const CASE_TYPE_LABELS = Object.freeze({
  [CASE_TYPES.INDIVIDUAL]: 'Individual',
  [CASE_TYPES.CORPORATE]: 'Corporate Entity',
  [CASE_TYPES.TRUST]: 'Trust',
  [CASE_TYPES.FUND]: 'Fund',
  [CASE_TYPES.FAMILY_OFFICE]: 'Family Office',
  [CASE_TYPES.SPE]: 'Special Purpose Entity',
});

// ─── Person Roles ───────────────────────────────────────────────────────
/** @enum {string} */
export const PERSON_ROLES = Object.freeze({
  APPLICANT: 'applicant',
  BENEFICIAL_OWNER: 'beneficial_owner',
  DIRECTOR: 'director',
  SHAREHOLDER: 'shareholder',
  AUTHORIZED_SIGNATORY: 'authorized_signatory',
  TRUSTEE: 'trustee',
  SETTLOR: 'settlor',
  BENEFICIARY: 'beneficiary',
  PROTECTOR: 'protector',
  FUND_MANAGER: 'fund_manager',
  NOMINEE: 'nominee',
  POWER_OF_ATTORNEY: 'power_of_attorney',
  COMPANY_SECRETARY: 'company_secretary',
  PARTNER: 'partner',
  CONTROLLING_PERSON: 'controlling_person',
});

export const PERSON_ROLE_LABELS = Object.freeze({
  [PERSON_ROLES.APPLICANT]: 'Applicant',
  [PERSON_ROLES.BENEFICIAL_OWNER]: 'Beneficial Owner',
  [PERSON_ROLES.DIRECTOR]: 'Director',
  [PERSON_ROLES.SHAREHOLDER]: 'Shareholder',
  [PERSON_ROLES.AUTHORIZED_SIGNATORY]: 'Authorised Signatory',
  [PERSON_ROLES.TRUSTEE]: 'Trustee',
  [PERSON_ROLES.SETTLOR]: 'Settlor',
  [PERSON_ROLES.BENEFICIARY]: 'Beneficiary',
  [PERSON_ROLES.PROTECTOR]: 'Protector',
  [PERSON_ROLES.FUND_MANAGER]: 'Fund Manager',
  [PERSON_ROLES.NOMINEE]: 'Nominee',
  [PERSON_ROLES.POWER_OF_ATTORNEY]: 'Power of Attorney',
  [PERSON_ROLES.COMPANY_SECRETARY]: 'Company Secretary',
  [PERSON_ROLES.PARTNER]: 'Partner',
  [PERSON_ROLES.CONTROLLING_PERSON]: 'Controlling Person',
});

// ─── Document Types ─────────────────────────────────────────────────────
/** @enum {string} */
export const DOC_TYPES = Object.freeze({
  // Individual documents
  NATIONAL_ID: 'national_id',
  PASSPORT: 'passport',
  DRIVERS_LICENSE: 'drivers_license',
  PROOF_OF_ADDRESS: 'proof_of_address',
  UTILITY_BILL: 'utility_bill',
  BANK_STATEMENT: 'bank_statement',
  TAX_CERTIFICATE: 'tax_certificate',
  TAX_RETURN: 'tax_return',
  SOURCE_OF_WEALTH: 'source_of_wealth',
  SOURCE_OF_FUNDS: 'source_of_funds',
  EMPLOYMENT_LETTER: 'employment_letter',
  SELFIE: 'selfie',
  PEP_DECLARATION: 'pep_declaration',

  // Corporate documents
  CERTIFICATE_OF_INCORPORATION: 'certificate_of_incorporation',
  ARTICLES_OF_ASSOCIATION: 'articles_of_association',
  MEMORANDUM_OF_ASSOCIATION: 'memorandum_of_association',
  REGISTER_OF_DIRECTORS: 'register_of_directors',
  REGISTER_OF_SHAREHOLDERS: 'register_of_shareholders',
  BOARD_RESOLUTION: 'board_resolution',
  CORPORATE_STRUCTURE_CHART: 'corporate_structure_chart',
  FINANCIAL_STATEMENTS: 'financial_statements',
  CERTIFICATE_OF_GOOD_STANDING: 'certificate_of_good_standing',
  BUSINESS_LICENSE: 'business_license',
  BENEFICIAL_OWNERSHIP_DECLARATION: 'beneficial_ownership_declaration',
  POWER_OF_ATTORNEY_DOC: 'power_of_attorney_doc',

  // Trust / Fund documents
  TRUST_DEED: 'trust_deed',
  FUND_PROSPECTUS: 'fund_prospectus',
  FUND_FACTSHEET: 'fund_factsheet',
  REGULATORY_LICENSE: 'regulatory_license',

  // Tax forms
  W8_BEN: 'w8_ben',
  W8_BEN_E: 'w8_ben_e',
  W9: 'w9',
  CRS_SELF_CERTIFICATION: 'crs_self_certification',

  // Other
  OTHER: 'other',
});

export const DOC_TYPE_LABELS = Object.freeze({
  [DOC_TYPES.NATIONAL_ID]: 'National ID',
  [DOC_TYPES.PASSPORT]: 'Passport',
  [DOC_TYPES.DRIVERS_LICENSE]: "Driver's Licence",
  [DOC_TYPES.PROOF_OF_ADDRESS]: 'Proof of Address',
  [DOC_TYPES.UTILITY_BILL]: 'Utility Bill',
  [DOC_TYPES.BANK_STATEMENT]: 'Bank Statement',
  [DOC_TYPES.TAX_CERTIFICATE]: 'Tax Certificate',
  [DOC_TYPES.TAX_RETURN]: 'Tax Return',
  [DOC_TYPES.SOURCE_OF_WEALTH]: 'Source of Wealth Declaration',
  [DOC_TYPES.SOURCE_OF_FUNDS]: 'Source of Funds Declaration',
  [DOC_TYPES.EMPLOYMENT_LETTER]: 'Employment Letter',
  [DOC_TYPES.SELFIE]: 'Selfie / Liveness Photo',
  [DOC_TYPES.PEP_DECLARATION]: 'PEP Declaration',
  [DOC_TYPES.CERTIFICATE_OF_INCORPORATION]: 'Certificate of Incorporation',
  [DOC_TYPES.ARTICLES_OF_ASSOCIATION]: 'Articles of Association',
  [DOC_TYPES.MEMORANDUM_OF_ASSOCIATION]: 'Memorandum of Association',
  [DOC_TYPES.REGISTER_OF_DIRECTORS]: 'Register of Directors',
  [DOC_TYPES.REGISTER_OF_SHAREHOLDERS]: 'Register of Shareholders',
  [DOC_TYPES.BOARD_RESOLUTION]: 'Board Resolution',
  [DOC_TYPES.CORPORATE_STRUCTURE_CHART]: 'Corporate Structure Chart',
  [DOC_TYPES.FINANCIAL_STATEMENTS]: 'Financial Statements',
  [DOC_TYPES.CERTIFICATE_OF_GOOD_STANDING]: 'Certificate of Good Standing',
  [DOC_TYPES.BUSINESS_LICENSE]: 'Business Licence',
  [DOC_TYPES.BENEFICIAL_OWNERSHIP_DECLARATION]: 'Beneficial Ownership Declaration',
  [DOC_TYPES.POWER_OF_ATTORNEY_DOC]: 'Power of Attorney Document',
  [DOC_TYPES.TRUST_DEED]: 'Trust Deed',
  [DOC_TYPES.FUND_PROSPECTUS]: 'Fund Prospectus',
  [DOC_TYPES.FUND_FACTSHEET]: 'Fund Factsheet',
  [DOC_TYPES.REGULATORY_LICENSE]: 'Regulatory Licence',
  [DOC_TYPES.W8_BEN]: 'W-8BEN',
  [DOC_TYPES.W8_BEN_E]: 'W-8BEN-E',
  [DOC_TYPES.W9]: 'W-9',
  [DOC_TYPES.CRS_SELF_CERTIFICATION]: 'CRS Self-Certification',
  [DOC_TYPES.OTHER]: 'Other',
});

// ─── Screening Types ────────────────────────────────────────────────────
/** @enum {string} */
export const SCREENING_TYPES = Object.freeze({
  PEP: 'pep',
  SANCTIONS: 'sanctions',
  ADVERSE_MEDIA: 'adverse_media',
  LAW_ENFORCEMENT: 'law_enforcement',
  RELATED_PARTIES: 'related_parties',
});

export const SCREENING_TYPE_LABELS = Object.freeze({
  [SCREENING_TYPES.PEP]: 'Politically Exposed Person',
  [SCREENING_TYPES.SANCTIONS]: 'Sanctions List',
  [SCREENING_TYPES.ADVERSE_MEDIA]: 'Adverse Media',
  [SCREENING_TYPES.LAW_ENFORCEMENT]: 'Law Enforcement',
  [SCREENING_TYPES.RELATED_PARTIES]: 'Related Parties',
});

// ─── Risk Ratings ───────────────────────────────────────────────────────
/** @enum {string} */
export const RISK_RATINGS = Object.freeze({
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  VERY_HIGH: 'very_high',
});

export const RISK_RATING_CONFIG = Object.freeze({
  [RISK_RATINGS.LOW]: {
    label: 'Low',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    badgeClass: 'bg-green-100 text-green-800',
    dotColor: 'bg-green-500',
  },
  [RISK_RATINGS.MEDIUM]: {
    label: 'Medium',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    badgeClass: 'bg-yellow-100 text-yellow-800',
    dotColor: 'bg-yellow-500',
  },
  [RISK_RATINGS.HIGH]: {
    label: 'High',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    badgeClass: 'bg-orange-100 text-orange-800',
    dotColor: 'bg-orange-500',
  },
  [RISK_RATINGS.VERY_HIGH]: {
    label: 'Very High',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    badgeClass: 'bg-red-100 text-red-800',
    dotColor: 'bg-red-500',
  },
});

// ─── Market Codes ───────────────────────────────────────────────────────
/** @enum {string} */
export const MARKET_CODES = Object.freeze({
  KE: 'KE',
  IN: 'IN',
  BR: 'BR',
  MX: 'MX',
  TR: 'TR',
  BD: 'BD',
  ZA: 'ZA',
  EG: 'EG',
  ET: 'ET',
});

export const MARKET_NAMES = Object.freeze({
  [MARKET_CODES.KE]: 'Kenya',
  [MARKET_CODES.IN]: 'India',
  [MARKET_CODES.BR]: 'Brazil',
  [MARKET_CODES.MX]: 'Mexico',
  [MARKET_CODES.TR]: 'Turkey',
  [MARKET_CODES.BD]: 'Bangladesh',
  [MARKET_CODES.ZA]: 'South Africa',
  [MARKET_CODES.EG]: 'Egypt',
  [MARKET_CODES.ET]: 'Ethiopia',
});

// ─── STR Suspicion Types ────────────────────────────────────────────────
/** @enum {string} */
export const STR_SUSPICION_TYPES = Object.freeze({
  MONEY_LAUNDERING: 'money_laundering',
  TERRORIST_FINANCING: 'terrorist_financing',
  SANCTIONS_EVASION: 'sanctions_evasion',
  FRAUD: 'fraud',
  TAX_EVASION: 'tax_evasion',
  BRIBERY_CORRUPTION: 'bribery_corruption',
  MARKET_MANIPULATION: 'market_manipulation',
  INSIDER_TRADING: 'insider_trading',
  PROLIFERATION_FINANCING: 'proliferation_financing',
  HUMAN_TRAFFICKING: 'human_trafficking',
  DRUG_TRAFFICKING: 'drug_trafficking',
  STRUCTURED_TRANSACTIONS: 'structured_transactions',
  UNEXPLAINED_WEALTH: 'unexplained_wealth',
  OTHER: 'other',
});

export const STR_SUSPICION_TYPE_LABELS = Object.freeze({
  [STR_SUSPICION_TYPES.MONEY_LAUNDERING]: 'Money Laundering',
  [STR_SUSPICION_TYPES.TERRORIST_FINANCING]: 'Terrorist Financing',
  [STR_SUSPICION_TYPES.SANCTIONS_EVASION]: 'Sanctions Evasion',
  [STR_SUSPICION_TYPES.FRAUD]: 'Fraud',
  [STR_SUSPICION_TYPES.TAX_EVASION]: 'Tax Evasion',
  [STR_SUSPICION_TYPES.BRIBERY_CORRUPTION]: 'Bribery & Corruption',
  [STR_SUSPICION_TYPES.MARKET_MANIPULATION]: 'Market Manipulation',
  [STR_SUSPICION_TYPES.INSIDER_TRADING]: 'Insider Trading',
  [STR_SUSPICION_TYPES.PROLIFERATION_FINANCING]: 'Proliferation Financing',
  [STR_SUSPICION_TYPES.HUMAN_TRAFFICKING]: 'Human Trafficking',
  [STR_SUSPICION_TYPES.DRUG_TRAFFICKING]: 'Drug Trafficking',
  [STR_SUSPICION_TYPES.STRUCTURED_TRANSACTIONS]: 'Structured Transactions',
  [STR_SUSPICION_TYPES.UNEXPLAINED_WEALTH]: 'Unexplained Wealth',
  [STR_SUSPICION_TYPES.OTHER]: 'Other',
});

// ─── Alert Types ────────────────────────────────────────────────────────
/** @enum {string} */
export const ALERT_TYPES = Object.freeze({
  DOCUMENT_EXPIRY: 'document_expiry',
  PERIODIC_REVIEW: 'periodic_review',
  SANCTIONS_HIT: 'sanctions_hit',
  PEP_STATUS_CHANGE: 'pep_status_change',
  ADVERSE_MEDIA_HIT: 'adverse_media_hit',
  TRANSACTION_ANOMALY: 'transaction_anomaly',
  COUNTRY_RISK_CHANGE: 'country_risk_change',
  OWNERSHIP_CHANGE: 'ownership_change',
  REGULATORY_UPDATE: 'regulatory_update',
  RISK_SCORE_CHANGE: 'risk_score_change',
  CASE_SLA_BREACH: 'case_sla_breach',
  INFORMATION_REQUEST_OVERDUE: 'information_request_overdue',
  ESCALATION_REQUIRED: 'escalation_required',
});

export const ALERT_TYPE_LABELS = Object.freeze({
  [ALERT_TYPES.DOCUMENT_EXPIRY]: 'Document Expiry',
  [ALERT_TYPES.PERIODIC_REVIEW]: 'Periodic Review Due',
  [ALERT_TYPES.SANCTIONS_HIT]: 'Sanctions Match',
  [ALERT_TYPES.PEP_STATUS_CHANGE]: 'PEP Status Change',
  [ALERT_TYPES.ADVERSE_MEDIA_HIT]: 'Adverse Media Match',
  [ALERT_TYPES.TRANSACTION_ANOMALY]: 'Transaction Anomaly',
  [ALERT_TYPES.COUNTRY_RISK_CHANGE]: 'Country Risk Change',
  [ALERT_TYPES.OWNERSHIP_CHANGE]: 'Ownership Change Detected',
  [ALERT_TYPES.REGULATORY_UPDATE]: 'Regulatory Update',
  [ALERT_TYPES.RISK_SCORE_CHANGE]: 'Risk Score Change',
  [ALERT_TYPES.CASE_SLA_BREACH]: 'Case SLA Breach',
  [ALERT_TYPES.INFORMATION_REQUEST_OVERDUE]: 'Information Request Overdue',
  [ALERT_TYPES.ESCALATION_REQUIRED]: 'Escalation Required',
});

// ─── Tax Frameworks ─────────────────────────────────────────────────────
/** @enum {string} */
export const TAX_FRAMEWORKS = Object.freeze({
  FATCA: 'fatca',
  CRS: 'crs',
});

export const TAX_FRAMEWORK_DETAILS = Object.freeze({
  [TAX_FRAMEWORKS.FATCA]: {
    label: 'FATCA',
    fullName: 'Foreign Account Tax Compliance Act',
    description:
      'US tax compliance requirement for foreign financial institutions to report ' +
      'information about financial accounts held by US taxpayers or foreign entities ' +
      'in which US taxpayers hold a substantial ownership interest.',
    administeredBy: 'US Internal Revenue Service (IRS)',
    forms: ['W-8BEN', 'W-8BEN-E', 'W-9'],
  },
  [TAX_FRAMEWORKS.CRS]: {
    label: 'CRS',
    fullName: 'Common Reporting Standard',
    description:
      'Global standard for the automatic exchange of financial account information ' +
      'between tax authorities, developed by the OECD. Requires financial institutions ' +
      'to identify customers who are tax residents of other participating jurisdictions.',
    administeredBy: 'OECD / Global Forum on Transparency',
    forms: ['CRS Self-Certification'],
  },
});
